import { FC, useMemo } from "react";
import { MuiDoubleLabel, MuiGrid } from "../../../../components";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import { getValueOrBlank } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ViewUserDataType } from "../../../createUser/modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../../../createUser/siteCore/SiteCoreKeys";

interface ViewUserUserDetailProps {
  userData: ViewUserDataType;
}

export const ViewUserUserDetail: FC<ViewUserUserDetailProps> = ({
  userData,
}) => {
  const { useFetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { fetchLanguageValueByKey } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getUserDetailLabel = useFetchLabel(
    createUserSiteCoreKeys.userDetailsKey,
  );

  const userSelectedData = useMemo(() => {
    const userStatus = userData?.isActive
      ? createUserSiteCoreKeys.statusActive
      : createUserSiteCoreKeys.statusInActive;
    const userStatusLabel = fetchValueByKeyOfDropdown(
      createUserSiteCoreKeys.userDetailsKey,
      createUserSiteCoreKeys.statusList,
      userStatus,
    );
    return { userStatus, userStatusLabel };
  }, [userData]);

  return (
    <MuiGrid container className="user-detail-grid">
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.preferredName)}
          replaceEmptyWithDash
          value={getValueOrBlank(userData?.username)}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(
            createUserSiteCoreKeys.userEmailPlaceHolder,
          )}
          replaceEmptyWithDash
          value={userData?.emailAddress}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.affiliateEmailId)}
          replaceEmptyWithDash
          value={userData?.affiliateEmailAddress}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.userCountry)}
          replaceEmptyWithDash
          value={getValueOrBlank(userData?.countryName)}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.homeOffice)}
          replaceEmptyWithDash
          value={userData?.officeName}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.applicationLanguage)}
          replaceEmptyWithDash
          value={fetchLanguageValueByKey(userData?.applicationLanguageCode)}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.timeZone)}
          replaceEmptyWithDash
          value={getValueOrBlank(userData?.timeZoneName)}
          isValueWithEllipsis
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.userStatus)}
          replaceEmptyWithDash
          value={
            <StatusBadge
              status={userSelectedData.userStatus as StatusBadgeType}
              label={userSelectedData.userStatusLabel}
            />
          }
          isValueWithEllipsis
        />
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getUserDetailLabel(createUserSiteCoreKeys.dateFormat)}
          replaceEmptyWithDash
          value={getValueOrBlank(userData?.userDateFormat?.name)}
          isValueWithEllipsis
        />
      </MuiGrid>
    </MuiGrid>
  );
};
